import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Download, Mail, Trash2, Ban, Copy, Settings as SettingsIcon, X, Loader2, AlertCircle, Eye, Repeat, Play } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  draft: "bg-gray-100 text-gray-700",
  sent: "bg-yellow-100 text-yellow-700",
  paid: "bg-green-100 text-green-700",
  overdue: "bg-red-100 text-red-700",
  cancelled: "bg-gray-100 text-gray-500",
};

const isPlaceholder = (v) => {
  if (!v) return true;
  const s = String(v).trim();
  return !s || (s.startsWith("[") && s.endsWith("]"));
};

const hasAnyBankDetails = (s) =>
  s && (!isPlaceholder(s.bank_account_name) || !isPlaceholder(s.bank_iban) ||
        !isPlaceholder(s.bank_account_number) || !isPlaceholder(s.bank_swift));

const CURRENCY_SYMBOL = { USD: "$", GBP: "GBP ", EUR: "EUR ", AED: "AED " };

const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "IE", name: "Ireland" },
  { code: "NL", name: "Netherlands" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "SG", name: "Singapore" },
  { code: "IN", name: "India" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "OTHER", name: "Other" },
];

const emptyForm = {
  client_name: "",
  client_email: "",
  client_company: "",
  client_address: "",
  client_country: "US",
  description: "",
  amount: "",
  currency: "USD",
  payment_terms: "Net 30",
  notes: "",
  billing_model: "other",
  is_engagement_fee: false,
  client_reference: "",
  deal_type: "solo",
  bd_partner_name: "",
};

const BILLING_MODELS = [
  { value: "priority_search", label: "Priority Search", desc: "25% of CTC · 90-day guarantee · 5% upfront + 20% on placement" },
  { value: "partnership_search", label: "Partnership Search", desc: "20% of CTC · 60-day guarantee · no upfront" },
  { value: "growth_partner", label: "Growth Partner (placement)", desc: "15% of CTC · 90-day guarantee · plus monthly retainer" },
  { value: "growth_partner_retainer", label: "Growth Partner (retainer)", desc: "Monthly retainer — use Recurring tab instead for auto-generation" },
  { value: "other", label: "Other", desc: "Ad-hoc advisory, consulting, or non-search billing" },
];

const fmtMoney = (amount, currency) =>
  `${CURRENCY_SYMBOL[currency] || ""}${Number(amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const InvoicesTab = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [vatPreview, setVatPreview] = useState({ rate: 0, vat: 0, total: 0 });
  const [subTab, setSubTab] = useState("invoices"); // invoices | recurring
  const [stats, setStats] = useState(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
  });

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/invoices`, getAuthHeaders());
      setInvoices(res.data || []);
    } catch {
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/invoices/settings`, getAuthHeaders());
      setSettings(res.data);
    } catch (error) {
      // Non-fatal: settings are optional on first load (singleton doc may not exist yet).
      console.debug("invoice settings fetch skipped:", error?.response?.status || error.message);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/invoices/stats/deal-types`, getAuthHeaders());
      setStats(res.data);
    } catch (error) {
      console.debug("deal-type stats fetch skipped:", error?.response?.status || error.message);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
    fetchSettings();
    fetchStats();
  }, [fetchInvoices, fetchSettings, fetchStats]);

  // Preview VAT as user types
  useEffect(() => {
    if (!settings || !form.amount) {
      setVatPreview({ rate: 0, vat: 0, total: Number(form.amount) || 0 });
      return;
    }
    const amt = Number(form.amount) || 0;
    let rate = 0;
    if (settings.vat_registered && form.client_country === "GB") {
      rate = Number(settings.default_vat_rate || 0.2);
    }
    const vat = Math.round(amt * rate * 100) / 100;
    setVatPreview({ rate, vat, total: Math.round((amt + vat) * 100) / 100 });
  }, [form.amount, form.client_country, settings]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: Number(form.amount),
        client_company: form.client_company || null,
        client_address: form.client_address || null,
        notes: form.notes || null,
        client_reference: form.client_reference || null,
        is_engagement_fee: !!form.is_engagement_fee && form.billing_model === "priority_search",
        deal_type: form.deal_type || "solo",
        bd_partner_name: form.deal_type === "split_desk" ? (form.bd_partner_name || null) : null,
      };
      await axios.post(`${API}/invoices`, payload, getAuthHeaders());
      toast.success("Invoice created and emailed to client");
      setShowForm(false);
      setForm(emptyForm);
      fetchInvoices();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create invoice");
    } finally {
      setSaving(false);
    }
  };

  const downloadPDF = async (invoice) => {
    try {
      const res = await axios.get(`${API}/invoices/${invoice.id}/pdf`, {
        ...getAuthHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoice.status === "paid" ? "Receipt" : "Invoice"}-${invoice.invoice_number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download PDF");
    }
  };

  const resend = async (invoice) => {
    try {
      const res = await axios.post(`${API}/invoices/${invoice.id}/resend`, {}, getAuthHeaders());
      if (res.data.sent) toast.success("Invoice email resent");
      else toast.error(res.data.error || "Email failed — check SendGrid API key");
    } catch {
      toast.error("Failed to resend email");
    }
  };

  const cancel = async (invoice) => {
    if (!window.confirm(`Cancel invoice ${invoice.invoice_number}?`)) return;
    try {
      await axios.patch(`${API}/invoices/${invoice.id}/cancel`, {}, getAuthHeaders());
      toast.success("Invoice cancelled");
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cannot cancel");
    }
  };

  const remove = async (invoice) => {
    if (!window.confirm(`Delete invoice ${invoice.invoice_number}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/invoices/${invoice.id}`, getAuthHeaders());
      toast.success("Invoice deleted");
      fetchInvoices();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const sendReminder = async (invoice) => {
    if (!window.confirm(`Send a payment reminder email to ${invoice.client_email}?`)) return;
    try {
      const res = await axios.post(`${API}/invoices/${invoice.id}/reminder`, {}, getAuthHeaders());
      if (res.data.sent) {
        toast.success(`Reminder sent (${res.data.days_overdue} days overdue)`);
      } else {
        toast.error(res.data.error || "Reminder email failed — check SendGrid API key");
      }
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reminder");
    }
  };

  const [detailsInvoice, setDetailsInvoice] = useState(null);

  const copyPayLink = (invoice) => {
    const url = `${window.location.origin}/pay/${invoice.invoice_number}`;
    navigator.clipboard.writeText(url);
    toast.success("Pay link copied");
  };

  return (
    <div>
      <div className="p-4 border-b border-gray-100 flex justify-between items-center flex-wrap gap-2">
        <div className="flex items-center gap-4">
          <h3 className="font-medium">Invoices</h3>
          <div className="flex gap-1 border border-gray-200">
            <button
              onClick={() => setSubTab("invoices")}
              className={`text-xs px-3 py-1.5 transition-colors ${subTab === "invoices" ? "bg-[#B8860B] text-white" : "hover:bg-gray-50"}`}
              data-testid="subtab-invoices"
            >
              One-off & Milestone
            </button>
            <button
              onClick={() => setSubTab("recurring")}
              className={`text-xs px-3 py-1.5 transition-colors flex items-center gap-1 ${subTab === "recurring" ? "bg-[#B8860B] text-white" : "hover:bg-gray-50"}`}
              data-testid="subtab-recurring"
            >
              <Repeat size={12} /> Recurring (Growth Partner)
            </button>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowSettings(true)}
            className="flex items-center gap-2 border border-gray-300 px-3 py-2 text-xs sm:text-sm hover:bg-gray-50"
            data-testid="invoices-settings-btn"
          >
            <SettingsIcon size={14} /> Settings
          </button>
          {subTab === "invoices" && (
            <button
              onClick={() => { setForm(emptyForm); setShowForm(true); }}
              className="flex items-center gap-2 bg-[#B8860B] text-white px-4 py-2 text-xs sm:text-sm hover:bg-[#9A7209]"
              data-testid="create-invoice-btn"
            >
              <Plus size={14} /> New Invoice
            </button>
          )}
        </div>
      </div>

      {subTab === "recurring" && <RecurringPanel settings={settings} />}

      {subTab === "invoices" && (<>
      {settings && (
        <div className="divide-y divide-gray-100 border-b">
          {!settings.vat_registered && (
            <div className="px-4 py-2 bg-yellow-50 text-yellow-800 text-xs flex items-center gap-2">
              <AlertCircle size={14} /> VAT is OFF. Invoices show the &quot;not VAT registered&quot; notice. Flip the toggle in Settings when you register.
            </div>
          )}
          {!hasAnyBankDetails(settings) && (
            <div className="px-4 py-2 bg-amber-50 text-amber-800 text-xs flex items-center gap-2">
              <AlertCircle size={14} /> Bank details are not configured. Invoices will show only the Stripe pay link (no bank transfer info). Add them in <button onClick={() => setShowSettings(true)} className="underline font-medium">Settings</button> if you want to offer bank transfer too.
            </div>
          )}
        </div>
      )}

      {subTab === "invoices" && stats && stats.total_count > 0 && (
        <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2 text-xs" data-testid="deal-mix-summary">
          <span className="font-medium text-gray-700">Deal mix:</span>
          <span className="px-2 py-1 bg-gray-100 rounded" data-testid="deal-mix-solo">Solo <strong>{stats.solo.count}</strong> · {fmtMoney(stats.solo.amount, "USD")}</span>
          <span className="px-2 py-1 bg-[#B8860B]/10 text-[#B8860B] rounded" data-testid="deal-mix-split">Split-Desk <strong>{stats.split_desk.count}</strong> · {fmtMoney(stats.split_desk.amount, "USD")}</span>
          <span className="text-gray-400">{Math.round((stats.split_desk.count / stats.total_count) * 100)}% team deals</span>
          <span className="text-gray-400" title="Fees summed nominally across currencies (mostly USD).">· fees nominal</span>
        </div>
      )}

      {loading ? (
        <div className="p-12 text-center text-gray-400">Loading…</div>
      ) : invoices.length === 0 ? (
        <div className="p-12 text-center text-gray-400">No invoices yet. Click &quot;New Invoice&quot; to create one.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Invoice</th>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Due</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`invoice-row-${inv.invoice_number}`}>
                  <td className="px-4 py-3 font-medium">
                    {inv.invoice_number}
                    {inv.deal_type === "split_desk" && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#B8860B]/10 text-[#B8860B] align-middle" data-testid={`deal-badge-${inv.invoice_number}`}>Split-Desk</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div>{inv.client_company || inv.client_name}</div>
                    <div className="text-xs text-gray-500">{inv.client_email}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-medium">{fmtMoney(inv.total_amount, inv.currency)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[inv.status] || "bg-gray-100"}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{inv.due_date}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => setDetailsInvoice(inv)} title="View details" className="p-1.5 text-gray-400 hover:text-[#B8860B]" data-testid={`view-${inv.invoice_number}`}>
                        <Eye size={15} />
                      </button>
                      <button onClick={() => downloadPDF(inv)} title="Download PDF" className="p-1.5 text-gray-400 hover:text-[#B8860B]" data-testid={`download-${inv.invoice_number}`}>
                        <Download size={15} />
                      </button>
                      <button onClick={() => copyPayLink(inv)} title="Copy pay link" className="p-1.5 text-gray-400 hover:text-[#B8860B]">
                        <Copy size={15} />
                      </button>
                      {inv.status !== "paid" && inv.status !== "cancelled" && (
                        <button onClick={() => resend(inv)} title="Resend invoice email" className="p-1.5 text-gray-400 hover:text-[#B8860B]">
                          <Mail size={15} />
                        </button>
                      )}
                      {(inv.status === "sent" || inv.status === "overdue") && (
                        <button onClick={() => sendReminder(inv)} title="Send payment reminder" className="p-1.5 text-gray-400 hover:text-orange-600" data-testid={`reminder-${inv.invoice_number}`}>
                          <AlertCircle size={15} />
                        </button>
                      )}
                      {inv.status !== "paid" && inv.status !== "cancelled" && (
                        <button onClick={() => cancel(inv)} title="Cancel invoice" className="p-1.5 text-gray-400 hover:text-orange-600">
                          <Ban size={15} />
                        </button>
                      )}
                      <button onClick={() => remove(inv)} title="Delete" className="p-1.5 text-gray-400 hover:text-red-600">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      </>)}

      {/* Create Invoice Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-display text-lg">New Invoice</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-600">Client Name *</label>
                  <input required value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="inv-client-name" />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Client Email *</label>
                  <input required type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="inv-client-email" />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Company</label>
                  <input value={form.client_company} onChange={(e) => setForm({ ...form, client_company: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Country *</label>
                  <select value={form.client_country} onChange={(e) => setForm({ ...form, client_country: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="inv-client-country">
                    {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-600">Billing Address</label>
                <input value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm" placeholder="Street, City, Postcode" />
              </div>
              <div>
                <label className="text-xs text-gray-600">Billing Model *</label>
                <select value={form.billing_model} onChange={(e) => setForm({ ...form, billing_model: e.target.value, is_engagement_fee: false })} className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="inv-billing-model">
                  {BILLING_MODELS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
                <p className="text-xs text-gray-500 mt-1">{BILLING_MODELS.find(m => m.value === form.billing_model)?.desc}</p>
              </div>
              {form.billing_model === "priority_search" && (
                <div className="p-3 bg-[#B8860B]/5 border border-[#B8860B]/30 rounded">
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={!!form.is_engagement_fee} onChange={(e) => setForm({ ...form, is_engagement_fee: e.target.checked })} className="mt-0.5" data-testid="inv-engagement-fee" />
                    <div>
                      <div className="font-medium">This is the 5% engagement fee (upfront)</div>
                      <div className="text-xs text-gray-600">Adds the 10-business-day refund clause to the PDF. Leave unchecked for the 20% placement invoice.</div>
                    </div>
                  </label>
                </div>
              )}
              {form.billing_model === "growth_partner_retainer" && (
                <div className="p-3 bg-blue-50 border border-blue-200 text-xs text-blue-900">
                  Tip: for automatic monthly retainers use the <strong>Recurring (Growth Partner)</strong> sub-tab instead. This creates only a one-off retainer invoice.
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 bg-gray-50 border border-gray-200 rounded">
                <div>
                  <label className="text-xs text-gray-600">Deal Type *</label>
                  <select value={form.deal_type} onChange={(e) => setForm({ ...form, deal_type: e.target.value, bd_partner_name: e.target.value === "solo" ? "" : form.bd_partner_name })} className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="inv-deal-type">
                    <option value="solo">Solo (one person won + delivered)</option>
                    <option value="split_desk">Split-Desk (BD originated + Recruiter delivered)</option>
                  </select>
                </div>
                {form.deal_type === "split_desk" && (
                  <div>
                    <label className="text-xs text-gray-600">BD Partner (who won the client)</label>
                    <input value={form.bd_partner_name} onChange={(e) => setForm({ ...form, bd_partner_name: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm" placeholder="e.g. Jordan Lee" data-testid="inv-bd-partner" />
                  </div>
                )}
                <p className="text-xs text-gray-500 md:col-span-2">Recorded for reporting only — does not change the invoice amount. On split-desk deals the pool (recruiter tier + 5 pts) is shared 40% BD / 60% recruiter.</p>
              </div>
              <div>
                <label className="text-xs text-gray-600">Client Reference / PO (optional)</label>
                <input value={form.client_reference} onChange={(e) => setForm({ ...form, client_reference: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm" placeholder="e.g. PO-2026-045" />
              </div>
              <div>
                <label className="text-xs text-gray-600">Description *</label>
                <input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm" placeholder="e.g. Executive Search – VP Engineering" data-testid="inv-description" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-gray-600">Amount *</label>
                  <input required type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="inv-amount" />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Currency</label>
                  <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm" data-testid="inv-currency">
                    <option value="USD">USD (Default)</option>
                    <option value="GBP">GBP</option>
                    <option value="EUR">EUR</option>
                    <option value="AED">AED</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600">Payment Terms</label>
                  <select value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm">
                    <option value="Net 7">Net 7</option>
                    <option value="Net 14">Net 14</option>
                    <option value="Net 30">Net 30</option>
                    <option value="Due on Receipt">Due on Receipt</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-600">Notes (optional)</label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-gray-300 px-3 py-2 text-sm" rows={2} />
              </div>

              {/* VAT Preview */}
              <div className="bg-gray-50 border border-gray-200 p-3 text-sm">
                <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>{fmtMoney(form.amount, form.currency)}</span></div>
                <div className="flex justify-between text-gray-600"><span>VAT ({(vatPreview.rate * 100).toFixed(0)}%)</span><span>{fmtMoney(vatPreview.vat, form.currency)}</span></div>
                <div className="flex justify-between font-medium text-base mt-1 pt-1 border-t border-gray-300"><span>Total</span><span className="text-[#B8860B]">{fmtMoney(vatPreview.total, form.currency)}</span></div>
                {vatPreview.rate === 0 && settings?.vat_registered && (
                  <div className="text-xs text-gray-500 mt-1">0% VAT (outside UK scope or reverse charge — applied automatically per country)</div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="bg-[#B8860B] text-white px-4 py-2 text-sm hover:bg-[#9A7209] flex items-center gap-2" data-testid="inv-submit">
                  {saving && <Loader2 size={14} className="animate-spin" />}
                  Create & Send
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && settings && (
        <InvoiceSettingsModal
          settings={settings}
          onClose={() => setShowSettings(false)}
          onSaved={(s) => { setSettings(s); setShowSettings(false); toast.success("Settings saved"); }}
        />
      )}

      {/* Details Drawer */}
      {detailsInvoice && (
        <InvoiceDetailsModal
          invoice={detailsInvoice}
          onClose={() => setDetailsInvoice(null)}
        />
      )}
    </div>
  );
};


const InvoiceDetailsModal = ({ invoice, onClose }) => {
  const payUrl = `${window.location.origin}/pay/${invoice.invoice_number}`;
  const reminders = invoice.reminders_sent || [];
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center">
          <div>
            <h3 className="font-display text-lg">{invoice.invoice_number}</h3>
            <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLORS[invoice.status] || "bg-gray-100"}`}>{invoice.status}</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <div><div className="text-xs text-gray-500">Client</div><div>{invoice.client_company || invoice.client_name}</div><div className="text-xs text-gray-500">{invoice.client_email}</div></div>
          <div><div className="text-xs text-gray-500">Description</div><div>{invoice.description}</div></div>
          <div className="grid grid-cols-2 gap-3">
            <div><div className="text-xs text-gray-500">Amount</div><div>{fmtMoney(invoice.amount, invoice.currency)}</div></div>
            <div><div className="text-xs text-gray-500">VAT ({(invoice.vat_rate * 100).toFixed(0)}%)</div><div>{fmtMoney(invoice.vat_amount, invoice.currency)}</div></div>
            <div><div className="text-xs text-gray-500">Total</div><div className="font-medium text-[#B8860B]">{fmtMoney(invoice.total_amount, invoice.currency)}</div></div>
            <div><div className="text-xs text-gray-500">Currency</div><div>{invoice.currency}</div></div>
            <div><div className="text-xs text-gray-500">Issued</div><div>{invoice.issue_date}</div></div>
            <div><div className="text-xs text-gray-500">Due</div><div>{invoice.due_date}</div></div>
            <div><div className="text-xs text-gray-500">Terms</div><div>{invoice.payment_terms}</div></div>
            <div><div className="text-xs text-gray-500">Country</div><div>{invoice.client_country}</div></div>
            <div><div className="text-xs text-gray-500">Deal Type</div><div>{invoice.deal_type === "split_desk" ? "Split-Desk (BD + Recruiter)" : "Solo"}</div></div>
            {invoice.deal_type === "split_desk" && invoice.bd_partner_name && (
              <div><div className="text-xs text-gray-500">BD Partner</div><div>{invoice.bd_partner_name}</div></div>
            )}
            {invoice.paid_at && <div className="col-span-2"><div className="text-xs text-gray-500">Paid On</div><div className="text-green-700">{invoice.paid_at.slice(0, 10)}</div></div>}
          </div>
          {invoice.notes && <div><div className="text-xs text-gray-500">Notes</div><div>{invoice.notes}</div></div>}
          <div>
            <div className="text-xs text-gray-500 mb-1">Pay Link</div>
            <div className="flex gap-2">
              <input readOnly value={payUrl} className="flex-1 border border-gray-200 px-2 py-1 text-xs bg-gray-50" />
              <button onClick={() => { navigator.clipboard.writeText(payUrl); toast.success("Copied"); }} className="border border-gray-300 px-2 text-xs hover:bg-gray-50">Copy</button>
            </div>
          </div>
          {reminders.length > 0 && (
            <div>
              <div className="text-xs text-gray-500 mb-1">Reminders Sent</div>
              <ul className="text-xs space-y-1">
                {reminders.map((r, i) => (
                  <li key={i} className="flex justify-between border-b border-gray-100 py-1">
                    <span>{r.sent_at?.slice(0, 10)} · {r.days_overdue}d overdue</span>
                    <span className={r.delivered ? "text-green-700" : "text-red-600"}>{r.delivered ? "delivered" : "failed"}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


const InvoiceSettingsModal = ({ settings, onClose, onSaved }) => {
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put(`${API}/invoices/settings`, form, {
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
      });
      onSaved(res.data);
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="font-display text-lg">Invoice Settings</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={save} className="p-4 space-y-4">
          <fieldset className="border border-gray-200 p-3">
            <legend className="text-xs text-gray-600 px-1">VAT</legend>
            <label className="flex items-center gap-2 text-sm mb-2">
              <input type="checkbox" checked={!!form.vat_registered} onChange={(e) => set("vat_registered", e.target.checked)} data-testid="vat-toggle" />
              VAT Registered (UK)
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-600">VAT Number</label>
                <input value={form.vat_number || ""} onChange={(e) => set("vat_number", e.target.value)} className="w-full border border-gray-300 px-3 py-2 text-sm" placeholder="GB123456789" />
              </div>
              <div>
                <label className="text-xs text-gray-600">Default VAT Rate</label>
                <input type="number" step="0.01" value={form.default_vat_rate} onChange={(e) => set("default_vat_rate", Number(e.target.value))} className="w-full border border-gray-300 px-3 py-2 text-sm" />
              </div>
            </div>
          </fieldset>

          <fieldset className="border border-gray-200 p-3">
            <legend className="text-xs text-gray-600 px-1">Company</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-600">Legal Name</label>
                <input value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} className="w-full border border-gray-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-600">Trading As</label>
                <input value={form.company_trading_as || ""} onChange={(e) => set("company_trading_as", e.target.value)} className="w-full border border-gray-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-600">Company No.</label>
                <input value={form.company_registration_number || ""} onChange={(e) => set("company_registration_number", e.target.value)} className="w-full border border-gray-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-600">Billing Email</label>
                <input type="email" value={form.billing_email || ""} onChange={(e) => set("billing_email", e.target.value)} className="w-full border border-gray-300 px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="mt-3">
              <label className="text-xs text-gray-600">Registered Address</label>
              <input value={form.company_address || ""} onChange={(e) => set("company_address", e.target.value)} className="w-full border border-gray-300 px-3 py-2 text-sm" />
            </div>
          </fieldset>

          <fieldset className="border border-gray-200 p-3">
            <legend className="text-xs text-gray-600 px-1">Bank Details (shown on invoice only if filled)</legend>
            <p className="text-xs text-gray-500 mb-2">Leave these blank and invoices will show only the Stripe pay link. Fill them in to offer bank transfer as an alternative.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-600">Account Name</label>
                <input value={form.bank_account_name && !form.bank_account_name.startsWith("[") ? form.bank_account_name : ""} onChange={(e) => set("bank_account_name", e.target.value)} className="w-full border border-gray-300 px-3 py-2 text-sm" placeholder="L&D Nexus Limited" />
              </div>
              <div>
                <label className="text-xs text-gray-600">Sort Code (UK)</label>
                <input value={form.bank_sort_code && !form.bank_sort_code.startsWith("[") ? form.bank_sort_code : ""} onChange={(e) => set("bank_sort_code", e.target.value)} className="w-full border border-gray-300 px-3 py-2 text-sm" placeholder="00-00-00" />
              </div>
              <div>
                <label className="text-xs text-gray-600">Account Number (UK)</label>
                <input value={form.bank_account_number && !form.bank_account_number.startsWith("[") ? form.bank_account_number : ""} onChange={(e) => set("bank_account_number", e.target.value)} className="w-full border border-gray-300 px-3 py-2 text-sm" placeholder="00000000" />
              </div>
              <div>
                <label className="text-xs text-gray-600">SWIFT/BIC</label>
                <input value={form.bank_swift && !form.bank_swift.startsWith("[") ? form.bank_swift : ""} onChange={(e) => set("bank_swift", e.target.value)} className="w-full border border-gray-300 px-3 py-2 text-sm" placeholder="e.g. BARCGB22" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-gray-600">IBAN (International)</label>
                <input value={form.bank_iban && !form.bank_iban.startsWith("[") ? form.bank_iban : ""} onChange={(e) => set("bank_iban", e.target.value)} className="w-full border border-gray-300 px-3 py-2 text-sm" placeholder="GB00 BARC 0000 0000 0000 00" />
              </div>
            </div>
          </fieldset>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="bg-[#B8860B] text-white px-4 py-2 text-sm hover:bg-[#9A7209] flex items-center gap-2" data-testid="save-invoice-settings">
              {saving && <Loader2 size={14} className="animate-spin" />}
              Save Settings
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};



// ============== Recurring Invoice Schedules (Growth Partner) ==============

const recurringEmpty = {
  client_name: "",
  client_email: "",
  client_company: "",
  client_address: "",
  client_country: "US",
  description: "Growth Partner monthly retainer",
  amount: 500,
  currency: "USD",
  payment_terms: "Net 7",
  day_of_month: 1,
  billing_model: "growth_partner",
};

const RecurringPanel = ({ settings }) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(recurringEmpty);
  const [saving, setSaving] = useState(false);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/invoices/recurring/all`, auth());
      setSchedules(res.data || []);
    } catch {
      toast.error("Failed to load recurring schedules");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/invoices/recurring`, { ...form, amount: Number(form.amount), day_of_month: Number(form.day_of_month) }, auth());
      toast.success("Recurring schedule created");
      setShowForm(false);
      setForm(recurringEmpty);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create schedule");
    } finally {
      setSaving(false);
    }
  };

  const togglePause = async (s) => {
    try {
      await axios.patch(`${API}/invoices/recurring/${s.id}`, { active: !s.active }, auth());
      toast.success(s.active ? "Schedule paused" : "Schedule resumed");
      fetchAll();
    } catch { toast.error("Failed to update"); }
  };

  const runNow = async (s) => {
    if (!window.confirm(`Generate an invoice NOW for ${s.client_company || s.client_name}? This will also email the client.`)) return;
    try {
      const res = await axios.post(`${API}/invoices/recurring/${s.id}/run-now`, {}, auth());
      toast.success(`Generated ${res.data.invoice_number}`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate");
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete the recurring schedule for ${s.client_company || s.client_name}? Past invoices are kept.`)) return;
    try {
      await axios.delete(`${API}/invoices/recurring/${s.id}`, auth());
      toast.success("Schedule deleted");
      fetchAll();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div>
      <div className="px-4 py-3 bg-blue-50 text-blue-900 text-xs border-b border-blue-100 flex items-start gap-2">
        <Repeat size={14} className="mt-0.5 shrink-0" />
        <div>
          <strong>Growth Partner retainers.</strong> Schedules auto-generate a branded invoice + Stripe pay link and email the client on the chosen day of each month. Typical: $500/mo or local equivalent.
        </div>
      </div>

      <div className="p-3 flex justify-end">
        <button onClick={() => { setForm(recurringEmpty); setShowForm(true); }}
                className="flex items-center gap-2 bg-[#B8860B] text-white px-4 py-2 text-xs hover:bg-[#9A7209]"
                data-testid="create-recurring-btn">
          <Plus size={14} /> New Recurring Schedule
        </button>
      </div>

      {loading ? (
        <div className="p-12 text-center text-gray-400">Loading…</div>
      ) : schedules.length === 0 ? (
        <div className="p-12 text-center text-gray-400">No recurring schedules. Click &quot;New Recurring Schedule&quot; to set one up for a Growth Partner client.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Day</th>
                <th className="text-left px-4 py-3">Next Run</th>
                <th className="text-left px-4 py-3">Generated</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {schedules.map(s => (
                <tr key={s.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div>{s.client_company || s.client_name}</div>
                    <div className="text-xs text-gray-500">{s.client_email}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-medium">{CURRENCY_SYMBOL[s.currency] || ""}{Number(s.amount).toLocaleString()}/mo</td>
                  <td className="px-4 py-3">{s.day_of_month}<sup>{s.day_of_month === 1 ? "st" : s.day_of_month === 2 ? "nd" : s.day_of_month === 3 ? "rd" : "th"}</sup></td>
                  <td className="px-4 py-3 text-xs">{s.next_run}</td>
                  <td className="px-4 py-3 text-xs">{(s.invoices_generated || []).length}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded ${s.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {s.active ? "active" : "paused"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => runNow(s)} title="Generate invoice now" className="p-1.5 text-gray-400 hover:text-[#B8860B]" data-testid={`run-now-${s.id.slice(0, 8)}`}>
                        <Play size={14} />
                      </button>
                      <button onClick={() => togglePause(s)} title={s.active ? "Pause" : "Resume"} className="p-1.5 text-gray-400 hover:text-orange-600">
                        <Ban size={14} />
                      </button>
                      <button onClick={() => remove(s)} title="Delete" className="p-1.5 text-gray-400 hover:text-red-600">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="font-display text-lg">New Recurring Schedule</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <form onSubmit={create} className="p-4 space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-600">Client Name *</label>
                  <input required value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="w-full border border-gray-300 px-3 py-2" data-testid="rec-client-name" />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Client Email *</label>
                  <input required type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} className="w-full border border-gray-300 px-3 py-2" data-testid="rec-client-email" />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Company</label>
                  <input value={form.client_company} onChange={(e) => setForm({ ...form, client_company: e.target.value })} className="w-full border border-gray-300 px-3 py-2" />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Country</label>
                  <select value={form.client_country} onChange={(e) => setForm({ ...form, client_country: e.target.value })} className="w-full border border-gray-300 px-3 py-2">
                    {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-600">Description</label>
                <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-gray-300 px-3 py-2" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-gray-600">Amount / month *</label>
                  <input required type="number" min="1" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full border border-gray-300 px-3 py-2" data-testid="rec-amount" />
                </div>
                <div>
                  <label className="text-xs text-gray-600">Currency</label>
                  <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="w-full border border-gray-300 px-3 py-2">
                    <option value="USD">USD</option><option value="GBP">GBP</option><option value="EUR">EUR</option><option value="AED">AED</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600">Billing day</label>
                  <input type="number" min="1" max="28" value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} className="w-full border border-gray-300 px-3 py-2" data-testid="rec-day" />
                </div>
              </div>
              <p className="text-xs text-gray-500">A branded invoice will be generated + emailed to the client on day {form.day_of_month} of each month. You can pause, run manually, or delete anytime.</p>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="bg-[#B8860B] text-white px-4 py-2 text-sm hover:bg-[#9A7209] flex items-center gap-2" data-testid="rec-submit">
                  {saving && <Loader2 size={14} className="animate-spin" />}
                  Create Schedule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};